// The questions each audit product asks a client, as reference content.
//
// SOURCES OF TRUTH, both in this repo:
//   - docs/specs/audit-intake.md §5 (which fields are client-facing vs internal),
//     §4 (how the three deck kinds derive) and §6.1 (step grouping, and what is
//     required to submit)
//   - docs/specs/audit-intake-notion-microcopy.md (the helper text, verbatim)
//
// The helper text is lifted word for word. It is already tested on real clients
// and the microcopy doc is the only place it exists outside the two live Notion
// forms, which the cutover archives.
//
// WHEN THE INTAKE FORM IS BUILT (docs/specs/audit-intake.md), it must import this
// module rather than restate the questions. Two copies of this content would
// drift, and the copy a client actually reads is the one that matters.
//
// TWO FORMS, THREE PRODUCTS. There are only two questionnaires — no-access and
// with-access — but three decks are sold from them (spec decision #6):
//
//   Viability   = the no-access form
//   Blind spot  = the with-access form
//   Combined    = the with-access form, then a top-up for the economics
//
// So the third product is composed here rather than being a third form. The
// client/internal split comes from the spec, NOT from whether a field has helper
// text — "Business name" has none and is client-facing.

export type AnswerType =
  | "text"
  | "long text"
  | "email"
  | "url"
  | "number"
  | "money"
  | "percent"
  | "choice"
  | "date"
  | "checkbox";

export type IntakeQuestion = {
  /** The `audit_intakes` column this becomes (spec §5). */
  field: string;
  label: string;
  type: AnswerType;
  /** Verbatim from the Notion form. Empty where Notion showed none. */
  helper?: string;
  /** Choice options, in the order the client sees them. */
  options?: string[];
  /** Required to SUBMIT, not to move between steps (spec §6.1). */
  required?: boolean;
};

export type IntakeStep = {
  title: string;
  /** Why the step sits where it does — spec §6.1's own reasoning. */
  note?: string;
  questions: IntakeQuestion[];
};

export type IntakeStage = {
  /** "Asked up front" vs "Asked in the top-up" for combined. */
  title?: string;
  note?: string;
  steps: IntakeStep[];
};

export type IntakeProduct = {
  /** Matches Audit::KINDS, so this lines up with the kind column on the index. */
  key: "viability" | "blind_spot" | "combined";
  name: string;
  /** Which questionnaire(s) it is gathered from. */
  form: string;
  summary: string;
  stages: IntakeStage[];
  /** Fields Matt fills in as the audit runs. Never asked of the client. */
  internal: IntakeQuestion[];
};

// --- the questions ---------------------------------------------------------
// Named individually so the three products can compose them without any of them
// restating a label or a piece of helper text.

const RUNNING_ADS: IntakeQuestion = {
  field: "running_google_ads",
  label: "Running Google Ads now?",
  type: "choice",
  helper: "Just so I know your starting point.",
  options: ["Running now", "Considering starting", "Ran before then stopped", "Not sure"],
};

const CAN_GRANT_ACCESS: IntakeQuestion = {
  field: "can_grant_access",
  label: "Could you give read-only access to the account?",
  type: "choice",
  options: ["Yes", "No", "Not sure"],
};

const BUSINESS_NAME: IntakeQuestion = {
  field: "business_name",
  label: "Business name",
  type: "text",
  required: true,
};

const WEBSITE: IntakeQuestion = {
  field: "website",
  label: "Website",
  type: "url",
  helper: "Just the main one is fine.",
  required: true,
};

const WHAT_YOU_DO: IntakeQuestion = {
  field: "what_you_do",
  label: "What does your business do?",
  type: "long text",
  helper: "A sentence or two on what you sell and who buys it.",
  required: true,
};

const SERVICES_WANTED: IntakeQuestion = {
  field: "services_wanted",
  label: "Which services do you want more leads for?",
  type: "long text",
  helper: "List the jobs worth the most to you. This tells me which searches to target.",
  required: true,
};

const AREAS_SERVED: IntakeQuestion = {
  field: "areas_served",
  label: "Which areas do you serve?",
  type: "long text",
  helper: "Towns, cities or regions you take work in. If it's nationwide, just say so.",
  required: true,
};

const IDEAL_CUSTOMER: IntakeQuestion = {
  field: "ideal_customer",
  label: "Describe your ideal customer or job",
  type: "long text",
  helper: "The kind of work you'd take more of tomorrow.",
};

const CURRENT_ADS_OR_LPS: IntakeQuestion = {
  field: "current_ads_or_lps",
  label: "Current ads or landing pages",
  type: "long text",
  helper: "Paste anything relevant. Helps me see where you're starting from.",
};

const CONTACT_NAME: IntakeQuestion = {
  field: "contact_name",
  label: "Your name",
  type: "text",
  helper: "So I know who I'm speaking to.",
};

const EMAIL: IntakeQuestion = {
  field: "email",
  label: "Best email to reach you",
  type: "email",
  helper: "Where I'll send the finished audit.",
  required: true,
};

const COMPETITORS: IntakeQuestion[] = [
  {
    field: "competitors",
    label: "Who are your main competitors?",
    type: "long text",
    helper: "Names are perfect. Even a couple helps me see who you're up against.",
  },
  {
    field: "competitor_websites",
    label: "Competitor websites",
    type: "long text",
    helper: "One per line. No worries if you don't have them.",
  },
  {
    field: "marketing_to_beat",
    label: "Marketing you rate or want to beat",
    type: "long text",
    helper: "Handy to know who to benchmark against.",
  },
];

const ECONOMICS: IntakeQuestion[] = [
  {
    field: "avg_job_value",
    label: "Average job value (£)",
    type: "money",
    helper: "Your best estimate is fine. This drives the whole forecast.",
    required: true,
  },
  {
    field: "monthly_budget",
    label: "Monthly budget in mind (£)",
    type: "money",
    helper:
      "Rough is fine, leave blank if you're not sure yet. I can model higher and lower budgets so you can see the trade-off.",
  },
  {
    field: "operating_margin",
    label: "Operating margin (%)",
    type: "percent",
    helper:
      "Optional. Lets me show profit, not just revenue. Skip it if you'd rather not share.",
  },
  {
    field: "lead_to_quote",
    label: "% of enquiries that become a quote",
    type: "percent",
    helper: "E.g. enter 40 if about 4 in 10. Leave blank if you don't track it.",
  },
  {
    field: "lead_to_sale",
    label: "% of enquiries that become a customer",
    type: "percent",
    helper:
      "Same idea. E.g. enter 10 if about 1 in 10. I'll use industry figures if you're unsure.",
  },
];

const CLOSING: IntakeStep = {
  title: "Anything else",
  note: "Short and easy, so the form ends on something light.",
  questions: [
    {
      field: "logo_url",
      label: "Logo / brand assets",
      type: "url",
      helper: "Drop your logo here if handy. Optional, I can source it.",
    },
    {
      field: "anything_else",
      label: "Anything else I should know?",
      type: "long text",
      helper: "Anything that would help me get this right.",
    },
  ],
};

const ROUTER: IntakeStep = {
  title: "First, two quick questions",
  note: "These decide which set of questions follows. Not running ads means there is no account to audit, so that route goes to viability whatever the access answer says.",
  questions: [RUNNING_ADS, CAN_GRANT_ACCESS],
};

// --- the two forms ---------------------------------------------------------

const NO_ACCESS_STEPS: IntakeStep[] = [
  ROUTER,
  {
    title: "About the business",
    note: "Easy to answer, and it is the qualifying information.",
    questions: [BUSINESS_NAME, WEBSITE, WHAT_YOU_DO, AREAS_SERVED, SERVICES_WANTED],
  },
  {
    title: "Your customers",
    note: "Still easy, and it gets the contact details captured early.",
    questions: [IDEAL_CUSTOMER, CURRENT_ADS_OR_LPS, CONTACT_NAME, EMAIL],
  },
  {
    title: "Competitors",
    note: "Opinion, not research. Nobody needs to go and look anything up.",
    questions: COMPETITORS,
  },
  {
    title: "The numbers",
    note: "The stall point, deliberately last — everything above is already banked if they stop here. Each of these carries an explicit \"I'm not sure, use an industry estimate\", which is a different answer from leaving it blank.",
    questions: ECONOMICS,
  },
  CLOSING,
];

const WITH_ACCESS_STEPS: IntakeStep[] = [
  ROUTER,
  {
    title: "About the business",
    note: "Same opening as the no-access form, minus the areas served — the account already shows where the spend goes.",
    questions: [BUSINESS_NAME, WEBSITE, WHAT_YOU_DO, SERVICES_WANTED],
  },
  {
    title: "Your customers",
    note: "No question about current ads or landing pages: those are visible in the account.",
    questions: [IDEAL_CUSTOMER, CONTACT_NAME, EMAIL],
  },
  CLOSING,
];

// Everything the with-access form does not ask. This is exactly what a top-up
// link presents when a blind-spot client wants the market side too (#7), and it
// is composed from the same questions rather than restated.
const TOP_UP_STEPS: IntakeStep[] = [
  {
    title: "Where you work",
    questions: [AREAS_SERVED],
  },
  {
    title: "What is running now",
    questions: [CURRENT_ADS_OR_LPS],
  },
  {
    title: "Competitors",
    note: "Opinion, not research.",
    questions: COMPETITORS,
  },
  {
    title: "The numbers",
    note: "The reason for the top-up: the forecast cannot be modelled without these.",
    questions: ECONOMICS,
  },
];

// --- internal tracker fields ------------------------------------------------

const STATUS_NO_ACCESS: IntakeQuestion = {
  field: "status",
  label: "Status",
  type: "choice",
  options: ["New", "Researching", "Checkpoint 1", "Drafting", "Checkpoint 2", "Delivered"],
};

const STATUS_WITH_ACCESS: IntakeQuestion = {
  field: "status",
  label: "Status",
  type: "choice",
  options: [
    "New",
    "Awaiting access",
    "Access granted",
    "Auditing",
    "Drafting",
    "Delivered",
    "No access - dropped",
  ],
};

const MARKET_TRACKER: IntakeQuestion[] = [
  { field: "curated_keywords", label: "Curated keywords (final 4)", type: "long text" },
  { field: "chosen_competitors", label: "Chosen competitors (1-2)", type: "long text" },
  { field: "cpc_used", label: "CPC used (£)", type: "money" },
  { field: "management_fee", label: "Management fee (£)", type: "money" },
  {
    field: "ad_captures",
    label: "Ad captures",
    type: "choice",
    options: ["Not started", "Route A", "Route B", "Done"],
  },
  { field: "lp_captures_done", label: "LP captures done", type: "checkbox" },
  { field: "keyword_export_url", label: "Keyword export", type: "url" },
];

const ACCOUNT_TRACKER: IntakeQuestion[] = [
  { field: "google_ads_customer_id", label: "Google Ads Customer ID", type: "text" },
  { field: "access_granted_at", label: "Access granted", type: "date" },
];

const NOTES: IntakeQuestion = {
  field: "internal_notes",
  label: "Internal notes",
  type: "long text",
};

// The score is one column; only its LABEL differs by kind, matching the deck
// slide it feeds (spec §5).
const OPPORTUNITY_SCORE: IntakeQuestion = {
  field: "score",
  label: "Overall Opportunity Score",
  type: "number",
};

const AUDIT_SCORE: IntakeQuestion = {
  field: "score",
  label: "Overall Audit Score",
  type: "number",
};

// --- the three products -----------------------------------------------------

export const INTAKE_PRODUCTS: IntakeProduct[] = [
  {
    key: "viability",
    name: "Viability",
    form: "No-access intake",
    summary:
      "For a prospect who is not running ads, or is but cannot give account access. There is no account to read, so everything is modelled from the market — which is why this is the form that asks for the economics.",
    stages: [{ steps: NO_ACCESS_STEPS }],
    internal: [STATUS_NO_ACCESS, OPPORTUNITY_SCORE, ...MARKET_TRACKER, NOTES],
  },
  {
    key: "blind_spot",
    name: "Blind spot",
    form: "With-access intake",
    summary:
      "For a prospect already running ads who can grant read-only access. The account answers most of what the viability form has to ask, so this drops the economics, the competitor research and the areas served entirely.",
    stages: [{ steps: WITH_ACCESS_STEPS }],
    internal: [STATUS_WITH_ACCESS, ...ACCOUNT_TRACKER, AUDIT_SCORE, NOTES],
  },
  {
    key: "combined",
    name: "Combined",
    form: "With-access intake, then a top-up",
    summary:
      "Not a third form. A combined deck is a record that has both — access granted and the full economics answered. The client fills the short with-access form, and a top-up link later asks only for what is missing.",
    stages: [
      {
        title: "Asked up front",
        note: "The with-access form, unchanged. Nothing about the combined product changes what they see first.",
        steps: WITH_ACCESS_STEPS,
      },
      {
        title: "Asked in the top-up",
        note: "Sent as a second link once the engagement grows. The record is the same one; answered fields carry over and they see only these.",
        steps: TOP_UP_STEPS,
      },
    ],
    internal: [
      STATUS_WITH_ACCESS,
      ...ACCOUNT_TRACKER,
      AUDIT_SCORE,
      ...MARKET_TRACKER,
      NOTES,
    ],
  },
];

export function stageCount(stage: IntakeStage): number {
  return stage.steps.reduce((total, step) => total + step.questions.length, 0);
}

export function questionCount(product: IntakeProduct): number {
  return product.stages.reduce((total, stage) => total + stageCount(stage), 0);
}

export function findProduct(key: string | null | undefined): IntakeProduct {
  return INTAKE_PRODUCTS.find((product) => product.key === key) ?? INTAKE_PRODUCTS[0];
}
